<!--
  Search. The query lives in the URL, so a result page can be shared, reloaded and
  reached with Back.

  WHERE THE FIELD IS DEPENDS ON THE SHELL. On a desktop it is the top bar's search
  icon, expanded. A phone's top bar is the bubble row and has no room for a field,
  and Search is one of its four tabs — so on a phone this screen owns the field and
  takes focus on arrival, which is what tapping a search tab is asking for.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Empty from '$lib/ui/Empty.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import TileGrid from '$lib/ui/TileGrid.svelte';
  import SearchField from '$lib/ui/SearchField.svelte';
  import DiscoverPaths from '$lib/ui/DiscoverPaths.svelte';
  import Skeleton from '$lib/ui/Skeleton.svelte';
  import Button from '$lib/ui/Button.svelte';
  import { search } from '$lib/ui/cinemeta';
  import { browseHref, nav, searchHref } from '$lib/ui/nav.svelte';
  import { clearQueries, forgetQuery, recent, rememberQuery } from '$lib/ui/prefs.svelte';
  import { shell } from '$lib/ui/shell.svelte';

  const query = $derived(nav.route.name === 'search' ? nav.route.q : '');

  let results = $state<CatalogItem[]>([]);
  let loading = $state(false);
  let error = $state('');
  let answered = $state('');

  $effect(() => {
    const q = query.trim();
    if (q.length < 2) {
      results = [];
      loading = false;
      error = '';
      answered = '';
      return;
    }

    const abort = new AbortController();
    loading = true;
    error = '';

    void search(q, abort.signal)
      .then((items) => {
        if (abort.signal.aborted) return;
        results = items;
        answered = q;
        loading = false;
        if (items.length > 0) rememberQuery(q);
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        error = 'The catalogue is not answering. Try again in a moment.';
        loading = false;
      });

    return () => abort.abort();
  });

  const skeletons = Array.from({ length: 12 }, (_, i) => i);

  /*
   * Dismissing the keyboard on THIS screen is what left the dock sitting high:
   * Search is unscrollable and autofocuses its field, so WebKit shifts the web
   * view to reveal the input and never puts it back. See keyboard-inset.ts for
   * the full account. Handled on blur as well as on the viewport event, because
   * this is the exact gesture and it needs nothing from visualViewport.
   */
  function settleAfterKeyboard(): void {
    requestAnimationFrame(() => {
      if (document.documentElement.scrollHeight <= window.innerHeight + 1) window.scrollTo(0, 0);
    });
  }
</script>

<svelte:head><title>{query ? `${query} | Jovii` : 'Search | Jovii'}</title></svelte:head>

<div class="page">
  <header class="search-head"><h1>{query.trim().length >= 2 ? `Results for “${query.trim()}”` : 'What are you in the mood for?'}</h1><p>{query.trim().length >= 2 ? 'Films and series, together in one search.' : 'Find a title you love, or take a different direction.'}</p></header>
  {#if shell.phone}
    <div class="field" onfocusout={settleAfterKeyboard}><SearchField autofocus /></div>
  {/if}

  {#if query.trim().length < 2}
    {#if recent.queries.length > 0}
      <section class="recent">
        <header>
          <h2>Recent searches</h2>
          <button type="button" onclick={clearQueries}>Clear</button>
        </header>
        <ul>
          {#each recent.queries as q (q)}
            <li>
              <a href={searchHref(q)}>
                <Icon name="search" size={15} />
                <span>{q}</span>
              </a>
              <!-- One row, taken back on its own — Clear above empties the lot. -->
              <button type="button" class="forget" onclick={() => forgetQuery(q)} aria-label="Forget “{q}”">
                <Icon name="close" size={14} />
              </button>
            </li>
          {/each}
        </ul>
      </section>
    {/if}
    <DiscoverPaths compact />
  {:else if loading && results.length === 0}
    <div class="grid">
      {#each skeletons as n (n)}
        <div class="cell">
          <Skeleton ratio="2 / 3" />
          <Skeleton width="85%" height="0.72rem" radius="5px" />
        </div>
      {/each}
    </div>
  {:else if error}
    <Empty icon="alert" tone="bad" title="Search failed" detail={error} />
  {:else if results.length === 0 && answered === query.trim()}
    <!-- Zero results is a dead end only if it offers nothing; the genre shelves
         are the honest other way in when the name will not come to mind. -->
    <Empty
      icon="search"
      title="Nothing called “{query}”"
      detail="Try fewer words, or the original-language title. Cinemeta matches on the exact name."
    >
      <Button href={browseHref('movie')} icon="film">Films by genre</Button>
      <Button href={browseHref('series')} icon="tv">Series by genre</Button>
    </Empty>
  {:else}
    <p class="count" role="status" aria-live="polite">{loading ? 'Searching… · ' : ''}{results.length} result{results.length === 1 ? '' : 's'}</p>
    <!-- TileGrid: the rail's focus/strip/tap manners on a wrapping grid. -->
    <TileGrid>
      {#each results as item, i (item.id)}
        <Poster {item} eager={i < 6} />
      {/each}
    </TileGrid>
  {/if}
</div>

<style>
  .page {
    padding: 16px max(var(--page-pad), var(--safe-r)) 40px max(var(--page-pad), var(--safe-l));
  }

  .search-head { margin: var(--space-5) 0 var(--space-5); }
  .search-head h1 { font-size: clamp(1.5rem, 3.2vw, 2.3rem); letter-spacing: var(--tracking-tight); line-height: 1.2; }
  .search-head p { margin-top: var(--space-2); color: var(--text-dim); line-height: 1.5; font-size: var(--text-sm); }
  .field {
    margin-bottom: var(--space-4);
  }

  .count {
    color: var(--text-faint);
    font-size: 0.76rem;
    margin-bottom: 12px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--tile-min), 1fr));
    gap: 18px var(--gap);
  }
  .cell {
    display: grid;
    gap: 7px;
  }

  .recent header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .recent h2 {
    font-size: 0.9rem;
    font-weight: 600;
  }
  .recent header button {
    color: var(--text-faint);
    font-size: 0.78rem;
  }
  .recent header button:hover {
    color: var(--text);
  }
  .recent ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .recent li {
    display: flex;
    align-items: center;
  }
  .recent li + li {
    border-top: 1px solid color-mix(in oklab, var(--line) 55%, transparent);
  }
  .recent a {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 2px;
    color: var(--text-dim);
    font-size: 0.87rem;
  }
  .recent a span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .recent a:hover {
    color: var(--text);
  }
  .forget {
    display: grid;
    place-content: center;
    width: 36px;
    height: 36px;
    flex: none;
    border-radius: var(--radius-pill);
    color: var(--text-faint);
  }
  .forget:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  @media (pointer: coarse) {
    .forget {
      width: var(--hit);
      height: var(--hit);
    }
  }

</style>
